
if (!window.__CartReviewController) {
  class CartReviewController {
    constructor(root) {
      this.root = root;
      this.config = this.parseConfig();

      this._onClick = this._onClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onCarouselScroll = this._onCarouselScroll.bind(this);
      this._onResize = this._onResize.bind(this);

      this.root.addEventListener("click", this._onClick);
      document.addEventListener("keydown", this._onKeydown);
      window.addEventListener("resize", this._onResize);

      this.track = this.root.querySelector("[data-cr-carousel-track]");
      if (this.track) this.track.addEventListener("scroll", this._onCarouselScroll, { passive: true });

      this._onCheckoutSubmit = this._onCheckoutSubmit.bind(this);
      this.checkoutForm = this.root.querySelector("[data-cr-checkout-form]");
      if (this.checkoutForm) this.checkoutForm.addEventListener("submit", this._onCheckoutSubmit);

      if (window.DeliverySchedule) {
        this.fillDates();
      } else {
        console.error("[cart-review] delivery-schedule.js not loaded; box dates not rendered");
      }
      this.updateCarouselArrows();
    }

    destroy() {
      this.root.removeEventListener("click", this._onClick);
      document.removeEventListener("keydown", this._onKeydown);
      window.removeEventListener("resize", this._onResize);
      if (this.track) this.track.removeEventListener("scroll", this._onCarouselScroll);
      if (this.checkoutForm) this.checkoutForm.removeEventListener("submit", this._onCheckoutSubmit);
    }

    // Returns {} rather than a fabricated cutoff: a made-up day with no
    // storeNow computes in UTC, which is the bug delivery-schedule.js exists to
    // fix. An empty config lets DEFAULT_CUTOFF_DAY apply as the single default.
    parseConfig() {
      const node = this.root.querySelector("[data-cart-review-config]");
      if (!node) return {};
      try {
        return JSON.parse(node.textContent.trim());
      } catch (e) {
        console.error("[cart-review] failed to parse config", e);
        return {};
      }
    }

    /* ---- Dates -------------------------------------------------- */

    // Mirrors the plan builder: the Sunday delivery is the first Sunday strictly
    // after the next cutoff (judged in STORE time); the second box delivers the
    // following Wednesday.
    nextDeliverySunday() {
      const c = this.config && this.config.cutoff;
      const storeNow = this.config && this.config.storeNow;
      return window.DeliverySchedule.nextDeliverySunday(
        c,
        window.DeliverySchedule.parseOffsetMinutes(storeNow)
      );
    }

    // The first box is the date chosen in the plan builder — cart attribute
    // `batch_1_delivery_date` (Sunday) or `batch_2_delivery_date` (a single-box
    // plan's Wednesday), ISO, rendered into the config — as long as it is still
    // deliverable: a cart left for a week can carry a date the cutoff has
    // since moved past, and a past date must not be shown as a promise.
    // Otherwise the earliest Sunday the cutoff allows, exactly as the builder
    // would default it.
    firstBoxDate() {
      const DS = window.DeliverySchedule;
      const earliest = this.nextDeliverySunday();
      const saved = DS.fromISODate(this.config && this.config.deliveryDate);
      return saved && saved.getTime() >= earliest.getTime() ? saved : earliest;
    }

    // Date for delivery box `index`: box 0 = the first box, box 1 = the
    // Wednesday three days on, and any further boxes step a week on from there.
    boxDate(index) {
      return window.DeliverySchedule.boxDate(this.firstBoxDate(), index);
    }

    /* ---- Delivery date check at checkout ------------------------ */

    // The order is dated from the basket's batch_1/2_delivery_date, but the
    // dates shown above fall back to the earliest Sunday when those are
    // missing or passed — so checkout reads the live cart first. A basket
    // left from before the order deadline, or one whose date write was lost,
    // is sent back to the plan builder to pick a date. A failed read never
    // blocks checkout.
    async _onCheckoutSubmit(event) {
      if (this._datesChecked) return;
      event.preventDefault();
      if (this._checkingDates) return;
      this._checkingDates = true;
      const form = event.currentTarget;
      const button = event.submitter || form.querySelector('[name="checkout"]');
      const DS = window.DeliverySchedule;
      let status = "ok";
      if (DS) {
        try {
          const base = (window.routes && window.routes.cart_url) || "/cart";
          const res = await fetch(`${base}.js`, { headers: { Accept: "application/json" } });
          if (!res.ok) throw new Error(`cart ${res.status}`);
          const cart = await res.json();
          const boxes = this.root.querySelectorAll("[data-cr-box-date]").length || 1;
          status = DS.cartDateStatus(cart.attributes, this.nextDeliverySunday(), boxes);
        } catch (err) {
          console.error("[cart-review] could not check the delivery date", err);
        }
      }
      this._checkingDates = false;
      if (status !== "ok") {
        this.showDateAlert(status);
        return;
      }
      this._datesChecked = true;
      if (form.requestSubmit) {
        form.requestSubmit(button || undefined);
      } else {
        // form.submit() skips the button, and its name="checkout" is what
        // sends the cart form on to checkout.
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "checkout";
        form.appendChild(input);
        form.submit();
      }
    }

    showDateAlert(status) {
      const alert = this.root.querySelector("[data-cr-date-alert]");
      if (!alert) return;
      const text = alert.querySelector("[data-cr-date-alert-text]");
      if (text) {
        text.textContent = status === "passed" ? alert.dataset.msgPassed : alert.dataset.msgMissing;
      }
      alert.hidden = false;
      alert.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }

    fillDates() {
      // Figma punctuates these "Sunday, 24 May" / "Sun, 24 May" (12425:15587,
      // 12425:15600). en-GB omits the comma when a weekday is combined with a
      // date in one call — it yields "Sunday 24 May" — so the weekday is
      // formatted separately and joined by hand.
      // timeZone: "UTC" reads these dates in the store's frame — see
      // assets/delivery-schedule.js.
      const withWeekday = (d, style, month) =>
        `${d.toLocaleDateString("en-GB", { weekday: style, timeZone: "UTC" })}, ${d.toLocaleDateString("en-GB", { day: "numeric", month, timeZone: "UTC" })}`;
      const long = (d) => withWeekday(d, "long", "long");
      const short = (d) => withWeekday(d, "short", "short");

      // A cart with one box has nothing to number against, so it reads
      // "Delivery on <date>" instead of "Box 1 on <date>". The cart cannot see
      // the plan's day count — it only knows how many _rc_bundle groups the
      // cart holds — so the box count is the signal. Matches the plan builder,
      // where the 3-day plan is exactly the single-delivery case.
      const boxEls = this.root.querySelectorAll("[data-cr-box-date]");
      const single = boxEls.length === 1;
      boxEls.forEach((el) => {
        const i = parseInt(el.dataset.boxIndex, 10) || 0;
        const date = `<strong>${long(this.boxDate(i))}</strong>`;
        el.innerHTML = single ? `Delivery on ${date}` : `Box ${i + 1} on ${date}`;
      });
      this.root.querySelectorAll("[data-cr-row-date]").forEach((el) => {
        const i = parseInt(el.dataset.boxIndex, 10) || 0;
        el.textContent = short(this.boxDate(i));
      });
      // "Delivery 1: <date>" in the summary's subscription card. Its own hook rather
      // than data-cr-box-date, whose element COUNT decides the wording above.
      this.root.querySelectorAll("[data-cr-delivery-date]").forEach((el) => {
        const i = parseInt(el.dataset.boxIndex, 10) || 0;
        el.textContent = long(this.boxDate(i));
      });
    }

    /* ---- Carousel ----------------------------------------------- */

    carouselStep() {
      if (!this.track) return 0;
      const card = this.track.querySelector("[data-cr-upsell]");
      const gap = parseInt(getComputedStyle(this.track).columnGap, 10) || 12;
      return card ? card.offsetWidth + gap : this.track.clientWidth;
    }

    scrollCarousel(dir) {
      if (!this.track) return;
      this.track.scrollBy({ left: dir * this.carouselStep(), behavior: "smooth" });
    }

    _onCarouselScroll() {
      this.updateCarouselArrows();
    }

    updateCarouselArrows() {
      if (!this.track) return;
      const prev = this.root.querySelector("[data-cr-carousel-prev]");
      const next = this.root.querySelector("[data-cr-carousel-next]");
      const max = this.track.scrollWidth - this.track.clientWidth - 1;
      if (prev) prev.disabled = this.track.scrollLeft <= 0;
      if (next) next.disabled = this.track.scrollLeft >= max;
    }

    _onResize() {
      this.updateCarouselArrows();
    }

    /* ---- Info modal --------------------------------------------- */

    openInfoModal(trigger) {
      const host = trigger.closest("[data-cr-meal], [data-cr-upsell]");
      const tpl = host && host.querySelector("[data-cr-info-content]");
      const modal = this.root.querySelector("[data-cr-info-modal]");
      if (!tpl || !modal) return;

      const titleEl = modal.querySelector("[data-cr-info-title]");
      const bodyEl = modal.querySelector("[data-cr-info-body]");
      if (titleEl) titleEl.textContent = tpl.dataset.title || "";
      if (bodyEl) {
        bodyEl.innerHTML = "";
        bodyEl.appendChild(tpl.content.cloneNode(true));
      }

      this._lastFocus = trigger;
      modal.hidden = false;
      document.body.style.overflow = "hidden";
      const closeBtn = modal.querySelector("[data-cr-info-close]");
      if (closeBtn && closeBtn.focus) closeBtn.focus();
    }

    closeInfoModal() {
      const modal = this.root.querySelector("[data-cr-info-modal]");
      if (!modal || modal.hidden) return;
      modal.hidden = true;
      document.body.style.overflow = "";
      if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
      this._lastFocus = null;
    }

    /* ---- Stub hooks (wired in a later milestone) ---------------- */

    onFrequencyToggle(control) {
      const open = control.getAttribute("aria-expanded") === "true";
      control.setAttribute("aria-expanded", open ? "false" : "true");
    }

    onAddUpsell(button) {
      // Stub: emit an event so a future milestone can add the item to the cart.
      this.root.dispatchEvent(new CustomEvent("cart-review:add-upsell", {
        bubbles: true,
        detail: {
          productId: button.dataset.productId,
          variantId: button.dataset.variantId,
        },
      }));
    }

    /* ---- Events ------------------------------------------------- */

    _onKeydown(event) {
      if (event.key === "Escape") {
        this.closeInfoModal();
        this.closeClearConfirm();
      }
    }

    _onClick(event) {
      if (event.target.closest("[data-cr-info-close]")) {
        this.closeInfoModal();
        return;
      }
      const infoBtn = event.target.closest("[data-cr-info]");
      if (infoBtn) {
        this.openInfoModal(infoBtn);
        return;
      }
      if (event.target.closest("[data-cr-carousel-prev]")) {
        this.scrollCarousel(-1);
        return;
      }
      if (event.target.closest("[data-cr-carousel-next]")) {
        this.scrollCarousel(1);
        return;
      }
      const freq = event.target.closest("[data-cr-frequency]");
      if (freq) {
        this.onFrequencyToggle(freq);
        return;
      }
      const addBtn = event.target.closest("[data-cr-add-upsell]");
      if (addBtn) {
        this.onAddUpsell(addBtn);
        return;
      }
      const clearBtn = event.target.closest("[data-cr-clear-cart]");
      if (clearBtn) {
        this.openClearConfirm(clearBtn);
        return;
      }
      if (event.target.closest("[data-cr-confirm-cancel]")) {
        this.closeClearConfirm();
        return;
      }
      const confirmOk = event.target.closest("[data-cr-clear-confirm]");
      if (confirmOk) {
        this.confirmClearCart(confirmOk);
        return;
      }
    }

    /* ---- Clear cart (inline confirm) --------------------------- */

    openClearConfirm(button) {
      const dialog = this.root.querySelector("[data-cr-confirm]");
      if (!dialog) return;
      this._clearUrl = button.dataset.clearUrl || "/cart/clear";
      this._clearTrigger = button;
      dialog.hidden = false;
      document.body.style.overflow = "hidden";
      const ok = dialog.querySelector("[data-cr-clear-confirm]");
      if (ok && ok.focus) ok.focus();
    }

    closeClearConfirm() {
      const dialog = this.root.querySelector("[data-cr-confirm]");
      if (!dialog || dialog.hidden) return;
      dialog.hidden = true;
      document.body.style.overflow = "";
      if (this._clearTrigger && this._clearTrigger.focus) this._clearTrigger.focus();
      this._clearTrigger = null;
    }

    confirmClearCart(button) {
      const url = this._clearUrl || "/cart/clear";
      button.disabled = true;
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
      })
        .then(() => { window.location.reload(); })
        .catch(() => { button.disabled = false; });
    }
  }

  window.__CartReviewController = CartReviewController;

  const init = (scope = document) => {
    scope.querySelectorAll("[data-cart-review]").forEach((root) => {
      if (root.__controller) return;
      root.__controller = new CartReviewController(root);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => init());
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", (e) => init(e.target));
  document.addEventListener("shopify:section:unload", (e) => {
    const root = e.target.querySelector("[data-cart-review]");
    if (root && root.__controller) {
      root.__controller.destroy();
      root.__controller = null;
    }
  });
}
